import React, { useMemo } from 'react'
import { useLocale } from 'src/lib/hooks/useLocal'
import NavigationMenuItem from './primary-tabs/NavigationMenuItem'
import { useCopilotOptional } from 'src/lib/copilot/useCopilot'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { useRouter } from 'next/router'
import { createNavigationMenuItems } from '../nav-items'
import { useNavigationRBAC } from 'src/lib/utils/navigation-rbac'
import { useUser } from 'src/lib/contexts/UserContext'
import useSWR from 'swr'
import { getMetaConnectionStatus } from 'src/lib/api/ads-iq'
import { staticOptions } from 'src/lib/core/api-client'
import { IconProps } from '@phosphor-icons/react'

export type NavigationItem = {
  id: string
  title: string
  icon: React.ComponentType<IconProps>
  path: string
  disabled: boolean
  rbacPermission: string | null
  children?: NavigationItem[]
  hidden?: boolean
  conditionallyHidden?: boolean
  showNewBadge?: boolean
}

const PrimaryTabs = () => {
  const { t } = useLocale()
  const router = useRouter()
  // Status-only slice: streaming tokens change `chat` identity constantly, but this
  // always-mounted nav should only re-render when the status actually transitions.
  // Null-safe: the provider is absent for users without TAB_COPILOT.
  const chatStatus = useCopilotOptional(ctx => ctx.chat.status, 'idle' as const)
  const copilotQueryInFlight = chatStatus === 'sending' || chatStatus === 'streaming'
  const { user, isUserConfigured } = useUser()
  const {
    getters: { isShopConnected, billingPopUp }
  } = useOrganisationContext()

  const { filterItems } = useNavigationRBAC()
  const hasOverdueInvoices = billingPopUp.hasOverdueInvoices && !user?.isSuperAdmin

  const hasAdsIqPermission = user ? (user.permissions?.some((p: any) => p.id === 'TAB_ADS_IQ') ?? false) : undefined
  const { data: metaData } = useSWR(
    hasAdsIqPermission ? 'ads-iq-connection-status' : null,
    async () => {
      const response: any = await getMetaConnectionStatus()
      return response?.data
    },
    { ...staticOptions }
  )
  const isMetaConnected = metaData?.connection?.status === 'connected'
  const hasEnabledPixels = (metaData?.pixels ?? []).some((p: any) => p.enabled)

  const allNavigationItems = useMemo(
    () => createNavigationMenuItems(t, isShopConnected, isUserConfigured, hasOverdueInvoices, { isMetaConnected, hasEnabledPixels, hasAdsIqPermission }),
    [t, isShopConnected, isUserConfigured, hasOverdueInvoices, user, isMetaConnected, hasEnabledPixels, hasAdsIqPermission]
  )

  const navigationMenuItems = useMemo(() => filterItems(allNavigationItems), [allNavigationItems, filterItems])

  const expandedMenuItem = useMemo(() => {
    const currentPath = router.asPath
    const menuItemWithCurrentPath = navigationMenuItems.find(item => {
      if (item.path === currentPath) return true
      if (item.children) {
        return item.children.some(child => child.path === currentPath)
      }
      return false
    })

    return menuItemWithCurrentPath?.title || null
  }, [router.asPath, navigationMenuItems])

  return (
    <div className='p-2 flex flex-col gap-0.5 w-full justify-start'>
      {navigationMenuItems.map(menuItem => (
        <NavigationMenuItem
          key={menuItem.title}
          menuItem={menuItem}
          isExpanded={expandedMenuItem === menuItem.title}
          showQueryPulse={menuItem.id === 'home' && copilotQueryInFlight}
        />
      ))}
    </div>
  )
}

export default PrimaryTabs
